import React, { useEffect, useState, useRef } from "react";
import { api, fmtDuration, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Download } from "lucide-react";

function ProjectModal({ open, onClose, onSaved, initial }) {
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [access, setAccess] = useState("Private");
  const [progress, setProgress] = useState("");
  const [billable, setBillable] = useState(false);
  const [active, setActive] = useState(true);
  const [studioHead, setStudioHead] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setName(initial.name); setClient(initial.client); setAccess(initial.access);
      setProgress(initial.progress || ""); setBillable(!!initial.billable); setActive(initial.active !== false);
      setStudioHead(initial.studio_head || "");
    } else {
      setName(""); setClient(""); setAccess("Private"); setProgress(""); setBillable(false); setActive(true);
      setStudioHead("");
    }
  }, [initial, open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name, client, access, progress: progress || null, billable, active, 
                      studio_head: studioHead || null
    };
      if (initial) await api.put(`/projects/${initial.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(initial ? "Updated" : "Created");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="project-modal">
      <div className="bg-white border border-gray-200 w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{initial ? "Edit Project" : "New Project"}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="ess-section-label block mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full border border-gray-200 px-3 py-2 text-sm" data-testid="project-name" />
          </div>
          <div>
            <label className="ess-section-label block mb-1">Client</label>
            <input value={client} onChange={(e) => setClient(e.target.value)} required className="w-full border border-gray-200 px-3 py-2 text-sm" data-testid="project-client" />
          </div>
          <div>
            <label className="ess-section-label block mb-1">Studio Head</label>
            <input 
              value={studioHead} 
              onChange={(e) => setStudioHead(e.target.value)} 
              placeholder="Enter studio head name" 
              className="w-full border border-gray-200 px-3 py-2 text-sm" 
              data-testid="project-studio-head" 
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ess-section-label block mb-1">Access</label>
              <select value={access} onChange={(e) => setAccess(e.target.value)} className="w-full border border-gray-200 px-3 py-2 text-sm" data-testid="project-access">
                <option>Private</option>
                <option>Public</option>
              </select>
            </div>
            <div>
              <label className="ess-section-label block mb-1">Progress</label>
              <input value={progress} onChange={(e) => setProgress(e.target.value)} placeholder="e.g. 40%" className="w-full border border-gray-200 px-3 py-2 text-sm" data-testid="project-progress" />
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} data-testid="project-billable" /> Billable</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="project-active" /> Active</label>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="border border-gray-200 px-4 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="bg-[var(--ess-ink)] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider" data-testid="project-save">{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper function to trigger file download
const triggerDownload = (blobData, kind, filename) => {
  const blob = new Blob([blobData], {
    type: kind === "xlsx"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "application/pdf",
  });
  
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `projects-report-${new Date().toISOString().slice(0, 10)}.${kind === "xlsx" ? "xlsx" : "pdf"}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [fActive, setFActive] = useState("all");
  const [fClient, setFClient] = useState("");
  const [fAccess, setFAccess] = useState("");
  const [fBillable, setFBillable] = useState("all");
  const [fStudioHead, setFStudioHead] = useState("");  // ✅ NEW: Studio Head filter state
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isExporting = useRef(false);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const r = await api.get("/projects", { params: search ? { search } : {} });
      setProjects(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [search]);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clients = Array.from(new Set(projects.map((p) => p.client))).filter(Boolean).sort();
  const studioHeads = Array.from(new Set(projects.map((p) => p.studio_head))).filter(Boolean).sort();  // ✅ NEW: Extract unique studio heads

  const filtered = projects.filter((p) => {
    if (fActive === "yes" && !p.active) return false;
    if (fActive === "no" && p.active) return false;
    if (fClient && p.client !== fClient) return false;
    if (fAccess && p.access !== fAccess) return false;
    if (fBillable === "yes" && !p.billable) return false;
    if (fBillable === "no" && p.billable) return false;
    if (fStudioHead && p.studio_head !== fStudioHead) return false;  // ✅ NEW: Apply studio head filter
    return true;
  });

  const clearFilters = () => { 
    setFActive("all"); 
    setFClient(""); 
    setFAccess(""); 
    setFBillable("all"); 
    setFStudioHead("");  // ✅ NEW: Clear studio head filter
    setSearch(""); 
  };

  // Export function
  const exportProjects = async (kind) => {
    if (isExporting.current) return;
    isExporting.current = true;

    const exportFilters = {
      active: fActive === "all" ? null : fActive === "yes",
      client: fClient || null,
      access: fAccess || null,
      billable: fBillable === "all" ? null : fBillable === "yes",
      search: search || null,
      studio_head: fStudioHead || null,  // ✅ NEW: Include studio head in export filters
    };

    try {
      toast.loading(`Starting ${kind.toUpperCase()} generation...`, { id: "export-toast" });
      
      const r = await api.post(`/projects/export/${kind}`, exportFilters, { 
        responseType: "blob" 
      });
      
      const arrayBuffer = await r.data.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const peekString = new TextDecoder().decode(uint8Array.slice(0, 100));
      let jsonData = null;
      
      try {
        if (peekString.trimStart().startsWith("{")) {
          const fullText = new TextDecoder().decode(uint8Array);
          jsonData = JSON.parse(fullText);
        }
      } catch (e) {}
      
      if (jsonData) {
        if (jsonData.job_id) {
          toast.loading(`Generating ${kind.toUpperCase()} in background. This may take a minute...`, { id: "export-toast" });
          
          const pollStatus = async () => {
            try {
              const statusRes = await api.get(`/projects/export/status/${jsonData.job_id}`, {
                responseType: 'blob'
              });
              
              const statusBuffer = await statusRes.data.arrayBuffer();
              const statusUint8 = new Uint8Array(statusBuffer);
              const statusPeek = new TextDecoder().decode(statusUint8.slice(0, 100));
              let statusJson = null;
              
              try {
                if (statusPeek.trimStart().startsWith("{")) {
                  const fullStatusText = new TextDecoder().decode(statusUint8);
                  statusJson = JSON.parse(fullStatusText);
                }
              } catch (e) {}
              
              if (statusJson) {
                if (statusJson.detail || statusJson.status === 'failed') {
                  toast.error(`Export failed: ${statusJson.detail || statusJson.error || 'Unknown error'}`, { id: "export-toast" });
                  isExporting.current = false;
                } else {
                  setTimeout(pollStatus, 3000);
                }
              } else {
                const fileBlob = new Blob([statusBuffer], {
                  type: kind === "xlsx"
                    ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    : "application/pdf",
                });
                const filename = `projects-report-${new Date().toISOString().slice(0, 10)}.${kind}`;
                triggerDownload(fileBlob, kind, filename);
                toast.success(`${kind.toUpperCase()} downloaded successfully!`, { id: "export-toast" });
                isExporting.current = false;
              }
            } catch (pollError) {
              toast.error("Failed to check export status", { id: "export-toast" });
              isExporting.current = false;
            }
          };
          
          setTimeout(pollStatus, 3000);
          
        } else if (jsonData.detail) {
          throw new Error(jsonData.detail);
        } else {
          throw new Error("Unexpected response from server");
        }
      } else {
        const fileBlob = new Blob([arrayBuffer], {
          type: kind === "xlsx"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "application/pdf",
        });
        const filename = `projects-report-${new Date().toISOString().slice(0, 10)}.${kind}`;
        triggerDownload(fileBlob, kind, filename);
        toast.success(`${kind.toUpperCase()} downloaded successfully!`, { id: "export-toast" });
        isExporting.current = false;
      }
      
    } catch (e) {
      let msg = "Export failed";
      if (e.message && !e.message.includes("Network Error")) {
        msg = e.message;
      } else {
        try {
          if (e.response?.data) {
            const text = typeof e.response.data === 'string' 
              ? e.response.data 
              : await e.response.data.text();
            const j = JSON.parse(text);
            msg = j.detail || j.message || msg;
          }
        } catch { }
      }
      toast.error(msg, { id: "export-toast" });
      isExporting.current = false;
    }
  };

  return (
    <div data-testid="projects-page">
      <div className="min-h-20 border-b border-gray-200 bg-white sticky top-0 z-20 flex items-center pl-16 lg:pl-8 pr-4 sm:pr-8 py-3 justify-between gap-3 flex-wrap">
        <div>
          <div className="ess-section-label">Manage</div>
          <div className="text-xl font-semibold tracking-tight text-[var(--ess-ink)]">Projects</div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => exportProjects("pdf")} 
            className="border border-gray-200 px-4 py-2.5 text-sm flex items-center gap-2 hover:border-[var(--ess-ink)]" 
            data-testid="export-projects-pdf"
          >
            <Download size={14} /> PDF
          </button>
          <button 
            onClick={() => exportProjects("xlsx")} 
            className="bg-[var(--ess-ink)] text-white px-4 py-2.5 text-sm flex items-center gap-2" 
            data-testid="export-projects-xlsx"
          >
            <Download size={14} /> Export XLSX
          </button>
          {isAdmin && (
            <button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[var(--ess-ink)] text-white px-4 py-2.5 text-sm font-semibold uppercase tracking-wider flex items-center gap-2" data-testid="new-project-btn">
              <Plus size={14} /> New Project
            </button>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-8 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find by name" className="border border-gray-200 px-4 py-2 text-sm w-64" data-testid="project-search" />
          <select value={fActive} onChange={(e) => setFActive(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-active">
            <option value="all">All (Active/Inactive)</option>
            <option value="yes">Active only</option>
            <option value="no">Inactive only</option>
          </select>
          <select value={fClient} onChange={(e) => setFClient(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-client">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {/* ✅ NEW: Studio Head Filter Dropdown */}
          <select value={fStudioHead} onChange={(e) => setFStudioHead(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-studio-head">
            <option value="">All studio heads</option>
            {studioHeads.map((sh) => <option key={sh} value={sh}>{sh}</option>)}
          </select>
          <select value={fAccess} onChange={(e) => setFAccess(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-access">
            <option value="">All access</option>
            <option value="Public">Public</option>
            <option value="Private">Private</option>
          </select>
          <select value={fBillable} onChange={(e) => setFBillable(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-billable">
            <option value="all">All billing</option>
            <option value="yes">Billable</option>
            <option value="no">Non-billable</option>
          </select>
          <button onClick={clearFilters} className="text-xs uppercase tracking-wider underline text-gray-500" data-testid="clear-filters">Clear filters</button>
          <span className="text-xs text-gray-500 ml-auto">{filtered.length} / {projects.length} projects</span>
        </div>

        <div className="border border-gray-200 overflow-x-auto">
          <table className="ess-table w-full min-w-[800px]">
            <thead>
              <tr>
                <th>Name</th>
                <th>Client</th>
                <th>Studio Head</th>
                <th>Tracked</th>
                <th>Progress</th>
                <th>Access</th>
                <th>Billing</th>
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-10 text-gray-500">No projects.</td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`project-row-${p.id}`}>
                  <td className="font-medium text-[var(--ess-ink)]">
                    {p.name}
                    {!p.active && <span className="ml-2 text-xs uppercase tracking-wider text-gray-400">inactive</span>}
                  </td>
                  <td>{p.client}</td>
                  <td>{p.studio_head || "—"}</td>
                  <td className="tabular-nums">{fmtDuration(p.tracked_seconds || 0)}</td>
                  <td>{p.progress || "—"}</td>
                  <td><span className="bg-gray-100 text-xs px-2 py-0.5">{p.access}</span></td>
                  <td className="text-xs">{p.billable ? "Billable" : "—"}</td>
                  {isAdmin && (
                    <td className="text-right">
                      <button onClick={() => { setEditing(p); setOpen(true); }} className="text-gray-500 hover:text-[var(--ess-ink)] mr-3" data-testid={`project-edit-${p.id}`}><Pencil size={14} /></button>
                      <button onClick={() => onDelete(p)} className="text-gray-400 hover:text-red-600" data-testid={`project-delete-${p.id}`}><Trash2 size={14} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ProjectModal open={open} onClose={() => setOpen(false)} onSaved={load} initial={editing} />
    </div>
  );
}
